import { createContext, useContext, useEffect, useState } from "react";

// Minimal i18n. Default language is EN (Part 1 of the V2 brief).
const KEY = "imaginary_lang";

const DICT = {
  en: {
    "nav.prints": "Prints",
    "nav.byArtist": "By artist",
    "nav.allPrints": "All prints",
    "nav.commissions": "Commissions",
    "nav.exhibitions": "Exhibitions",
    "nav.concept": "The Concept",
    "cart.title": "Cart",
    "cart.empty": "Your cart is empty.",
    "cart.discover": "Discover the editions",
    "cart.freeRemaining": "away from free shipping",
    "cart.freeUnlocked": "Free shipping unlocked",
    "cart.shippingZone": "Shipping to",
    "cart.zoneFR": "France",
    "cart.zoneEU": "Europe",
    "cart.subtotal": "Subtotal",
    "cart.shipping": "Shipping",
    "cart.free": "Free",
    "cart.total": "Total",
    "cart.checkout": "Checkout",
    "cart.checkoutLoading": "Redirecting…",
    "cart.guest": "Guest checkout — no account needed. Card, Apple Pay, Google Pay.",
    "cart.continue": "Continue browsing",
    "cart.remove": "Remove",
    "cart.editionOf": "Edition of",
    "cart.remaining": "remaining",
    "cart.error": "Payment could not start. Please try again.",
    "shop.kicker": "Prints — Numbered Editions",
    "shop.title.a": "The ",
    "shop.title.x": "X",
    "shop.title.b": " Goes Here — in print.",
    "shop.intro":
      "Museum-quality fine-art prints, made to order. Three sizes, framed or unframed, Noir or Bi-color.",
    "shop.from": "from",
    "shop.upcoming":
      "New editions are released in waves — join the newsletter for first access.",
    "shop.almostGone": "Almost gone",
    "product.back": "Prints",
    "product.kicker": "Numbered edition",
    "product.color": "Colourway",
    "product.size": "Size",
    "product.framing": "Framing",
    "product.frameColor": "Frame",
    "product.unframed": "Unframed",
    "product.framed": "Framed",
    "product.oak": "Natural oak",
    "product.blackOak": "Black oak",
    "product.remaining": "of",
    "product.remainingTail": "remaining",
    "product.almostGone": "Almost gone",
    "product.soldOut": "Sold out",
    "product.notify": "Notify me",
    "product.notifyThanks": "Noted — you will be first to know.",
    "product.notifyPlaceholder": "Your email",
    "product.add": "Add to cart",
    "product.added": "Added",
    "product.sizeGuide": "Size guide",
    "product.sizeNote":
      "Compared against a standard two-seat sofa (180 cm). Buyers consistently misjudge print size — when in doubt, go one size up.",
    "product.d.paper.t": "Paper & printing",
    "product.d.paper.b":
      "Hahnemühle German Etching 310 g/m², 100% cotton, 12-colour pigment ink. Museum quality, made to order, deep matte finish.",
    "product.d.framing.t": "Framing",
    "product.d.framing.b":
      "Solid oak frame, natural or black stained, acrylic glazing, ready to hang. Framed by hand in Paris.",
    "product.d.edition.t": "Edition & authenticity",
    "product.d.edition.b":
      "Each print is numbered within its edition and ships with a signed certificate of authenticity.",
    "product.d.shipping.t": "Shipping & delivery",
    "product.d.shipping.b":
      "Printed to order, hand-numbered and signed in the studio. Unframed prints ship rolled in a tube, framed pieces in a protective crate. 10–14 working days (framed 70×100: specialist carrier, 12–20 days). Free shipping over €150.",
    "product.concept":
      "A hand-painted cross where the masterpiece should be. The work that is missing says more about the room than the work that is there. Hang it where the painting you dream of owning would live.",
    "product.completeWall": "Complete the wall",
    "product.disclaimer":
      "Noir shown in a black frame, Bi-color in natural oak. In-situ views are indicative.",
    "product.notFound": "Artwork not found.",
    "product.backToShop": "Back to prints",
    "success.title": "Thank you for your order.",
    "success.checking": "Confirming your payment…",
    "success.body":
      "Your payment is confirmed. You will receive a Stripe receipt and VAT invoice by email. Your print is produced to order, hand-numbered and signed in the studio, then dispatched within 10–14 working days.",
    "success.pending": "Payment is being verified",
    "success.pendingBody":
      "If your payment was charged, your order will appear. You can close this page.",
    "success.back": "Back to the prints",
    "footer.newsletter.title": "Newsletter",
    "footer.newsletter.body":
      "First access to new editions, released in waves. No discount — just first in line.",
    "footer.newsletter.placeholder": "Your email",
    "footer.newsletter.button": "Subscribe",
    "footer.newsletter.thanks": "Welcome. You will be first to know.",
    "footer.col.navigate": "Navigate",
    "footer.col.contact": "Contact",
    "footer.col.legal": "Legal",
    "footer.legal.shipping": "Shipping & Returns",
    "footer.legal.cgv": "Terms of Sale (CGV)",
    "footer.legal.mentions": "Mentions légales",
    "footer.legal.privacy": "Privacy (GDPR)",
    "footer.legal.cookies": "Cookies",
    "footer.legal.withdrawal": "Withdrawal",
    "exh.kicker": "Exhibitions — Case studies",
    "exh.title.a": "The X, ",
    "exh.title.i": "installed.",
    "exh.title.b": "",
    "exh.intro":
      "Proof of work: the gesture beyond the print — murals, gallery walls and private collections. Concept visuals are labelled as such.",
    "exh.conceptVisual": "Concept visual",
    "exh.type": "Type",
    "exh.format": "Format",
    "exh.location": "Place",
    "exh.cta": "Commission a wall like this",
    "exh.press": "Press & collaborations",
    "exh.pressBody":
      "Nothing here yet — and that's the point of a young house. For press, partnerships and foundation projects: hello@thexgoeshere.com.",
    "artist.allArtists": "All artists",
    "artist.majorWorks": "Major works",
    "artist.theX": "The X Goes Here",
    "artist.conceptNote":
      "Each print in the collection is a numbered edition, made to order on Hahnemühle German Etching 310 g/m² — the cross hand-painted in spirit, the absence guaranteed.",
    "artist.viewPrint": "Discover the print",
    "artist.otherArtists": "Other artists",
    "artists.kicker": "The Artists",
    "artists.title.a": "Twenty-one ",
    "artists.title.i": "absent masters.",
    "artists.intro":
      "The stories, the masterpieces and the ideas behind every name in the collection — and the cross that keeps their place on your wall.",
    "nav.mto": "Made to Order",
    "trust.printing.t": "Museum-grade printing",
    "trust.printing.b": "12-colour pigment Giclée on Hahnemühle German Etching 310 g/m², 100% cotton.",
    "trust.editions.t": "Numbered editions",
    "trust.editions.b": "Hand-numbered and signed in the studio. Never reprinted.",
    "trust.france.t": "Made in France",
    "trust.france.b": "Printed and finished in France, shipped across Europe.",
    "fulfil.line": "Printed to order, hand-numbered and signed in the studio. Shipped within 10–14 working days.",
    "fulfil.specialist": "Framed 70 × 100 ships with a specialist carrier — allow 12–20 working days.",
    "collector.label": "Signed collector edition",
    "collector.note": "Edition of 15 — hand-numbered, signed, certificate of authenticity. Unframed.",
    "quick.shop": "Quick shop",
    "grid.remaining": "remaining",
    "grid.soldOut": "Sold out",
    "grid.notifyPlaceholder": "Email — notify me",
    "mto.kicker": "Made to Order",
    "mto.title.a": "The ",
    "mto.title.blank": "______",
    "mto.title.b": " Goes Here.",
    "mto.intro": "Not on the wall yet? Name it yourself. Every X is a placeholder for something you want.",
    "mto.wordLabel": "Your word or name",
    "mto.wordPlaceholder": "Riviera, Apollo, Grand-mère…",
    "mto.unique": "Unique — made for you. One-off, never numbered.",
    "mto.blocked.protected": "This name belongs to its rightful owners — we can't make it. But its edition may be in the collection:",
    "mto.blocked.protectedBrand": "This name is protected — we can't manufacture it. Try your own word instead.",
    "mto.blocked.profanity": "This word cannot be printed. Try another.",
    "mto.review": "Every Made to Order piece is reviewed by hand in the studio before printing.",
    "mto.legal": "I understand this personalised piece is made for me and is exempt from the 14-day right of withdrawal (art. L221-28, French Consumer Code).",
    "mto.add": "Add my one-off to the cart",
    "mto.legalRequired": "Please acknowledge the personalisation terms first.",
    "aw.kicker": "Art Walls",
    "aw.title.a": "Three crosses, ",
    "aw.title.i": "one wall.",
    "aw.intro": "Three 40 × 50 prints in different colourways — 195 € instead of 225 €. Curated sets, or build your own.",
    "aw.curated": "Curated walls",
    "aw.build": "Build your own",
    "aw.pick": "Pick",
    "aw.addWall": "Add the wall — 195 €",
    "aw.pickMore": "Choose {n} more",
    "aw.yourWall": "Your wall",
    "aw.saving": "You save 30 €",
    "exit.title.a": "First access to ",
    "exit.title.i": "new editions.",
    "exit.body": "Editions are released in waves and never reprinted. Leave your email — be first in line, and get 10% off your first order.",
    "exit.button": "Get my 10% code",
    "exit.code": "Your code, valid on your first order:",
    "exit.noThanks": "No thanks",
    "journal.kicker": "Journal",
    "journal.title.a": "Stories from ",
    "journal.title.i": "behind the X.",
    "journal.intro": "Rooms, interviews and case studies — cheap to read, dangerous to want.",
    "journal.read": "Read the story",
    "journal.shopThe": "Featured in this story",
    "comm.professionals": "For professionals",
    "comm.professionalsBody": "Interior architects and designers: trade terms on request. Write to commissions@thexgoeshere.com.",
  },
  fr: {
    "nav.prints": "Éditions",
    "nav.byArtist": "Par artiste",
    "nav.allPrints": "Tous les tirages",
    "nav.commissions": "Commissions",
    "nav.exhibitions": "Expositions",
    "nav.concept": "Le Concept",
    "cart.title": "Panier",
    "cart.empty": "Votre panier est vide.",
    "cart.discover": "Découvrir les éditions",
    "cart.freeRemaining": "de la livraison offerte",
    "cart.freeUnlocked": "Livraison offerte débloquée",
    "cart.shippingZone": "Livraison",
    "cart.zoneFR": "France",
    "cart.zoneEU": "Europe",
    "cart.subtotal": "Sous-total",
    "cart.shipping": "Livraison",
    "cart.free": "Offerte",
    "cart.total": "Total",
    "cart.checkout": "Payer",
    "cart.checkoutLoading": "Redirection…",
    "cart.guest": "Paiement sans compte — carte, Apple Pay, Google Pay.",
    "cart.continue": "Continuer mes achats",
    "cart.remove": "Retirer",
    "cart.editionOf": "Édition de",
    "cart.remaining": "restants",
    "cart.error": "Le paiement n'a pas pu démarrer. Réessayez.",
    "shop.kicker": "Éditions numérotées",
    "shop.title.a": "The ",
    "shop.title.x": "X",
    "shop.title.b": " Goes Here — en tirage.",
    "shop.intro":
      "Tirages d'art de qualité musée, imprimés à la demande. Trois formats, encadrés ou non, Noir ou Bi-color.",
    "shop.from": "dès",
    "shop.upcoming":
      "Les nouvelles éditions sortent par vagues — inscrivez-vous à la newsletter pour un accès prioritaire.",
    "shop.almostGone": "Dernières pièces",
    "product.back": "Éditions",
    "product.kicker": "Édition numérotée",
    "product.color": "Coloris",
    "product.size": "Format",
    "product.framing": "Encadrement",
    "product.frameColor": "Cadre",
    "product.unframed": "Sans cadre",
    "product.framed": "Encadré",
    "product.oak": "Chêne naturel",
    "product.blackOak": "Chêne noir",
    "product.remaining": "sur",
    "product.remainingTail": "restants",
    "product.almostGone": "Dernières pièces",
    "product.soldOut": "Épuisé",
    "product.notify": "Me prévenir",
    "product.notifyThanks": "C'est noté — vous serez prévenu en premier.",
    "product.notifyPlaceholder": "Votre email",
    "product.add": "Ajouter au panier",
    "product.added": "Ajouté",
    "product.sizeGuide": "Guide des formats",
    "product.sizeNote":
      "Comparés à un canapé deux places standard (180 cm). On sous-estime souvent la taille d'un tirage — en cas de doute, prenez le format au-dessus.",
    "product.d.paper.t": "Papier & impression",
    "product.d.paper.b":
      "Hahnemühle German Etching 310 g/m², 100 % coton, encre pigmentaire 12 couleurs. Qualité musée, impression à la demande, fini mat profond.",
    "product.d.framing.t": "Encadrement",
    "product.d.framing.b":
      "Cadre en chêne massif, naturel ou teinté noir, verre acrylique, prêt à accrocher. Encadrement main à Paris.",
    "product.d.edition.t": "Édition & authenticité",
    "product.d.edition.b":
      "Chaque tirage est numéroté dans son édition et livré avec un certificat d'authenticité signé.",
    "product.d.shipping.t": "Livraison",
    "product.d.shipping.b":
      "Imprimé à la demande, numéroté et signé à la main à l'atelier. Les tirages sans cadre voyagent roulés en tube, les encadrés en caisse de protection. 10 à 14 jours ouvrés (70×100 encadré : transporteur spécialisé, 12 à 20 jours). Livraison offerte dès 150 €.",
    "product.concept":
      "Une croix peinte à la main, là où le chef-d'œuvre devrait être. L'œuvre qui manque en dit plus sur la pièce que celle qui s'y trouve. Accrochez-la à la place du tableau que vous rêvez de posséder.",
    "product.completeWall": "Complétez le mur",
    "product.disclaimer":
      "Noir présenté en cadre noir, Bi-color en chêne naturel. Mises en situation à titre indicatif.",
    "product.notFound": "Œuvre introuvable.",
    "product.backToShop": "Retour aux éditions",
    "success.title": "Merci pour votre commande.",
    "success.checking": "Confirmation de votre paiement…",
    "success.body":
      "Votre paiement est confirmé. Vous recevrez un reçu Stripe et une facture TVA par email. Votre tirage est produit à la demande, numéroté et signé à la main à l'atelier, puis expédié sous 10 à 14 jours ouvrés.",
    "success.pending": "Paiement en cours de vérification",
    "success.pendingBody":
      "Si votre paiement a été débité, votre commande apparaîtra bien. Vous pouvez fermer cette page.",
    "success.back": "Retour aux éditions",
    "footer.newsletter.title": "Newsletter",
    "footer.newsletter.body":
      "Accès prioritaire aux nouvelles éditions, publiées par vagues. Pas de remise — juste la première place.",
    "footer.newsletter.placeholder": "Votre email",
    "footer.newsletter.button": "S'inscrire",
    "footer.newsletter.thanks": "Bienvenue. Vous serez prévenu en premier.",
    "footer.col.navigate": "Naviguer",
    "footer.col.contact": "Contact",
    "footer.col.legal": "Légal",
    "footer.legal.shipping": "Livraison & retours",
    "footer.legal.cgv": "CGV",
    "footer.legal.mentions": "Mentions légales",
    "footer.legal.privacy": "Confidentialité (RGPD)",
    "footer.legal.cookies": "Cookies",
    "footer.legal.withdrawal": "Rétractation",
    "exh.kicker": "Expositions — Études de cas",
    "exh.title.a": "Le X, ",
    "exh.title.i": "installé.",
    "exh.title.b": "",
    "exh.intro":
      "La preuve par l'œuvre : le geste au-delà du tirage — fresques, murs de galerie et collections privées. Les visuels conceptuels sont indiqués comme tels.",
    "exh.conceptVisual": "Visuel conceptuel",
    "exh.type": "Type",
    "exh.format": "Format",
    "exh.location": "Lieu",
    "exh.cta": "Commander un mur comme celui-ci",
    "exh.press": "Presse & collaborations",
    "exh.pressBody":
      "Rien ici pour l'instant — c'est aussi ça, une jeune maison. Presse, partenariats et projets de fondations : hello@thexgoeshere.com.",
    "artist.allArtists": "Tous les artistes",
    "artist.majorWorks": "Œuvres majeures",
    "artist.theX": "The X Goes Here",
    "artist.conceptNote":
      "Chaque tirage de la collection est une édition numérotée, imprimée à la demande sur Hahnemühle German Etching 310 g/m² — la croix peinte à la main dans l'esprit, l'absence garantie.",
    "artist.viewPrint": "Découvrir le tirage",
    "artist.otherArtists": "Autres artistes",
    "artists.kicker": "Les Artistes",
    "artists.title.a": "Vingt-et-un ",
    "artists.title.i": "maîtres absents.",
    "artists.intro":
      "Les histoires, les chefs-d'œuvre et les idées derrière chaque nom de la collection — et la croix qui garde leur place sur votre mur.",
    "nav.mto": "Sur Mesure",
    "trust.printing.t": "Impression musée",
    "trust.printing.b": "Giclée pigmentaire 12 couleurs sur Hahnemühle German Etching 310 g/m², 100 % coton.",
    "trust.editions.t": "Éditions numérotées",
    "trust.editions.b": "Numérotées et signées à la main à l'atelier. Jamais réimprimées.",
    "trust.france.t": "Fabriqué en France",
    "trust.france.b": "Imprimé et fini en France, expédié dans toute l'Europe.",
    "fulfil.line": "Imprimé à la demande, numéroté et signé à la main à l'atelier. Expédié sous 10 à 14 jours ouvrés.",
    "fulfil.specialist": "Le 70 × 100 encadré voyage par transporteur spécialisé — comptez 12 à 20 jours ouvrés.",
    "collector.label": "Édition collector signée",
    "collector.note": "Édition de 15 — numérotée à la main, signée, certificat d'authenticité. Sans cadre.",
    "quick.shop": "Achat rapide",
    "grid.remaining": "restants",
    "grid.soldOut": "Épuisé",
    "grid.notifyPlaceholder": "Email — me prévenir",
    "mto.kicker": "Sur Mesure",
    "mto.title.a": "The ",
    "mto.title.blank": "______",
    "mto.title.b": " Goes Here.",
    "mto.intro": "Pas encore sur le mur ? Nommez-le vous-même. Chaque X est la place réservée de ce que vous désirez.",
    "mto.wordLabel": "Votre mot ou prénom",
    "mto.wordPlaceholder": "Riviera, Apollo, Grand-mère…",
    "mto.unique": "Unique — fabriqué pour vous. Pièce unique, jamais numérotée.",
    "mto.blocked.protected": "Ce nom appartient à ses ayants droit — nous ne pouvons pas le fabriquer. Mais son édition est peut-être dans la collection :",
    "mto.blocked.protectedBrand": "Ce nom est protégé — nous ne pouvons pas le fabriquer. Essayez votre propre mot.",
    "mto.blocked.profanity": "Ce mot ne peut pas être imprimé. Essayez-en un autre.",
    "mto.review": "Chaque pièce Sur Mesure est relue à la main à l'atelier avant impression.",
    "mto.legal": "Je comprends que cette pièce personnalisée est fabriquée pour moi et n'est pas couverte par le droit de rétractation de 14 jours (art. L221-28 du Code de la consommation).",
    "mto.add": "Ajouter ma pièce unique",
    "mto.legalRequired": "Merci de confirmer les conditions de personnalisation.",
    "aw.kicker": "Art Walls",
    "aw.title.a": "Trois croix, ",
    "aw.title.i": "un mur.",
    "aw.intro": "Trois tirages 40 × 50 en coloris différents — 195 € au lieu de 225 €. Ensembles composés, ou créez le vôtre.",
    "aw.curated": "Murs composés",
    "aw.build": "Composez le vôtre",
    "aw.pick": "Choix",
    "aw.addWall": "Ajouter le mur — 195 €",
    "aw.pickMore": "Encore {n} choix",
    "aw.yourWall": "Votre mur",
    "aw.saving": "Vous économisez 30 €",
    "exit.title.a": "Accès prioritaire aux ",
    "exit.title.i": "nouvelles éditions.",
    "exit.body": "Les éditions sortent par vagues et ne sont jamais réimprimées. Laissez votre email — passez devant tout le monde, et recevez -10 % sur votre première commande.",
    "exit.button": "Recevoir mon code -10 %",
    "exit.code": "Votre code, valable sur votre première commande :",
    "exit.noThanks": "Non merci",
    "journal.kicker": "Journal",
    "journal.title.a": "Histoires ",
    "journal.title.i": "derrière le X.",
    "journal.intro": "Intérieurs, entretiens et études de cas — gratuits à lire, dangereux à désirer.",
    "journal.read": "Lire l'article",
    "journal.shopThe": "Dans cet article",
    "comm.professionals": "Professionnels",
    "comm.professionalsBody": "Architectes d'intérieur et décorateurs : conditions pro sur demande. Écrivez à commissions@thexgoeshere.com.",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem(KEY) || "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, lang);
    } catch {}
  }, [lang]);

  const t = (key) => DICT[lang][key] ?? DICT.en[key] ?? key;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
